import React, { useState, useEffect, useCallback } from 'react';
import { View, TouchableOpacity, StyleSheet, Platform, useWindowDimensions, Modal, Pressable, ViewStyle } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../../context/ThemeContext';
import { RootStackParamList } from '../../navigation/types';
import { Typography } from '../ui';
import { BrandLogo } from '../BrandLogo';
import { headerFill, pageFill, platformBackgrounds } from '../../styles/platformBackgrounds';
import { useI18n, changeLanguage } from '../../i18n';
import { useLanguageSwitcherEnabled } from '../../hooks/useFeatureFlags';
import { landingLayout } from './constants';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const MOBILE_BREAKPOINT = 768;

const THEME_ICONS: Record<string, keyof typeof Ionicons.glyphMap> = {
  light: 'sunny',
  dark: 'moon',
  system: 'desktop-outline',
};

type LandingLanguage = 'ru' | 'en';
const LANGUAGE_OPTIONS: LandingLanguage[] = ['ru', 'en'];

export const Header: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { width } = useWindowDimensions();
  const { mode, isDark, colors: themeColors, cycleMode } = useTheme();
  const { t, i18n } = useI18n('landing');
  const rawLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const currentLang: LandingLanguage = rawLang === 'ru' ? 'ru' : 'en';
  const setLanguage = useCallback((lang: LandingLanguage) => {
    if (lang !== currentLang) {
      void changeLanguage(lang);
    }
  }, [currentLang]);
  // CORE-1715 — тот же флаг `language_switcher`, что и у основной шапки:
  // `/landing` открывается по прямой ссылке, и переключатель здесь — такое же
  // место в вебе, где его видно. Прячется целиком, вместе с рамкой и глобусом.
  const languageSwitcherEnabled = useLanguageSwitcherEnabled();
  const isDesktop = Platform.OS === 'web' && width >= MOBILE_BREAKPOINT;
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleScroll = () => setIsScrolled(window.scrollY > 20);
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <View
      style={[
        styles.header,
        {
          backgroundColor: isDark ? 'rgba(18,18,18,0.95)' : 'rgba(255,255,255,0.95)',
          borderBottomColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'
        },
        // CORE-1733 — шапка лендинга та же, что у платформы: фон и линия v4.
        Platform.OS === 'web' && {
          ...headerFill(isDark ? 'dark' : 'light'),
          borderBottomColor: platformBackgrounds[isDark ? 'dark' : 'light'].headerBorder,
        },
        isScrolled && styles.headerScrolled
      ]}
      className={Platform.OS === 'web' ? (isScrolled ? 'header-blur' : 'header-noble') : ''}
    >
      <View style={styles.container}>
        <View style={styles.leftSection}>
          <TouchableOpacity onPress={() => navigation.navigate('Landing')} style={styles.logoContainer}>
            <BrandLogo height={44} />
          </TouchableOpacity>
        </View>

        <View style={styles.rightSection}>
          {isDesktop && (
            <View style={styles.authActions}>
              {languageSwitcherEnabled && (
                <View style={[styles.langSwitch, { borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }]}>
                  {LANGUAGE_OPTIONS.map((lang) => {
                    const isActive = currentLang === lang;
                    return (
                      <TouchableOpacity
                        key={lang}
                        onPress={() => setLanguage(lang)}
                        style={[styles.langOption, isActive && { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                        activeOpacity={0.75}
                        accessibilityLabel={`Switch language to ${lang.toUpperCase()}`}
                        testID={`header-lang-${lang}`}
                      >
                        <Typography style={[styles.langToggleText, { color: isActive ? themeColors.textPrimary : themeColors.textSecondary }]}>
                          {lang.toUpperCase()}
                        </Typography>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
              <TouchableOpacity onPress={cycleMode} style={styles.themeToggle} activeOpacity={0.7}>
                <Ionicons name={THEME_ICONS[mode]} size={18} color={themeColors.textSecondary} />
              </TouchableOpacity>
              {/* The same outlined account action as the site-wide header. */}
              <TouchableOpacity accessibilityRole="button" onPress={() => navigation.navigate('Login')} style={[styles.loginBtn, { height: 44, justifyContent: 'center', paddingHorizontal: 24, borderRadius: 22, borderWidth: 1, borderColor: isDark ? '#ADB7AF' : '#9CA797', backgroundColor: isDark ? 'rgba(255,254,247,0.04)' : 'rgba(255,254,247,0.55)' }]} activeOpacity={0.7}>
                <Typography variant="button" style={[styles.loginLink, { color: isDark ? '#F0ECE4' : '#314C48' }]}>{t('common:buttons.logIn')}</Typography>
              </TouchableOpacity>
            </View>
          )}

          {!isDesktop && (
            <TouchableOpacity style={styles.menuBtn} onPress={() => setMobileMenuOpen(true)}>
              <Ionicons name="menu" size={28} color={themeColors.textPrimary} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Mobile Menu */}
      <Modal
        visible={mobileMenuOpen}
        animationType="slide"
        transparent
        onRequestClose={() => setMobileMenuOpen(false)}
      >
        <Pressable style={styles.mobileOverlay} onPress={() => setMobileMenuOpen(false)}>
          <Pressable
            style={[
              styles.mobileMenu,
              // CORE-1733 — меню во весь экран — это страница: общий фон v4.
              Platform.OS === 'web' ? pageFill(isDark ? 'dark' : 'light') : { backgroundColor: isDark ? '#1D1B22' : '#FBFAF8' },
            ]}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={styles.mobileMenuHeader}>
              <BrandLogo height={44} />
              <TouchableOpacity onPress={() => setMobileMenuOpen(false)} style={styles.menuBtn}>
                <Ionicons name="close" size={28} color={themeColors.textPrimary} />
              </TouchableOpacity>
            </View>

            <View style={styles.mobileNav} />

            <View style={[styles.mobileActions, { borderTopColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }]}>
              <TouchableOpacity
                style={styles.mobileAuthBtn}
                onPress={() => { setMobileMenuOpen(false); navigation.navigate('Login'); }}
                activeOpacity={0.7}
              >
                <Ionicons name="log-in-outline" size={22} color={themeColors.textPrimary} />
                <Typography variant="button" style={[styles.mobileAuthText, { color: themeColors.textPrimary }]}>{t('common:buttons.logIn')}</Typography>
              </TouchableOpacity>
              {languageSwitcherEnabled && (
                <View style={styles.mobileThemeToggle}>
                  <Ionicons name="globe-outline" size={20} color={themeColors.textSecondary} />
                  <View style={[styles.mobileLangSwitch, { borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }]}>
                    {LANGUAGE_OPTIONS.map((lang) => {
                      const isActive = currentLang === lang;
                      return (
                        <TouchableOpacity
                          key={lang}
                          onPress={() => setLanguage(lang)}
                          style={[styles.mobileLangOption, isActive && { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                          activeOpacity={0.75}
                          accessibilityLabel={`Switch language to ${lang.toUpperCase()}`}
                        >
                          <Typography variant="caption" style={{ color: isActive ? themeColors.textPrimary : themeColors.textSecondary }}>
                            {lang.toUpperCase()}
                          </Typography>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              )}
              <TouchableOpacity onPress={cycleMode} style={styles.mobileThemeToggle} activeOpacity={0.7}>
                <Ionicons name={THEME_ICONS[mode]} size={20} color={themeColors.textSecondary} />
                <Typography variant="caption" style={{ color: themeColors.textSecondary, textTransform: 'capitalize' }}>
                  {t('theme.mode', { mode })}
                </Typography>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 90,
    zIndex: 9999,
    backgroundColor: 'rgba(255, 255, 255, 0.95)', // Solid background with slight transparency
    backdropFilter: 'blur(20px)', // Glass effect
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
    transition: 'all 0.4s ease',
  } as any,
  headerScrolled: {
    height: 80,
  } as ViewStyle,
  container: {
    maxWidth: landingLayout.maxWidth,
    marginHorizontal: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
    paddingHorizontal: landingLayout.paddingH,
    width: '100%',
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 60,
  },
  logoContainer: {
    paddingVertical: 10,
  },
  logoImage: {
    height: 36,
    width: 120,
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  authActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 32,
  },
  menuBtn: {
    padding: 8,
  },
  loginBtn: {
    marginLeft: 8,
  },
  loginLink: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 1.5,
    opacity: 0.8,
  },
  mobileAuthBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
  },
  mobileAuthText: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 1.5,
  },
  mobileOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  mobileMenu: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    paddingTop: Platform.OS === 'ios' ? 60 : 20,
    paddingHorizontal: 24,
    paddingBottom: 40,
  },
  mobileMenuHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 48,
  },
  mobileNav: {
    flex: 1,
    gap: 8,
  },
  mobileActions: {
    gap: 16,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.06)',
  },
  themeToggle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.7,
  },
  langSwitch: {
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    padding: 2,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  } as ViewStyle,
  langOption: {
    minWidth: 34,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  } as ViewStyle,
  langToggleText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  mobileLangSwitch: {
    borderRadius: 18,
    borderWidth: 1,
    padding: 2,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  } as ViewStyle,
  mobileLangOption: {
    minWidth: 38,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
  } as ViewStyle,
  mobileThemeToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
  },
});
